import { Button, Heading, LuIcon, Menu, Separator, Text } from "@overmap-ai/blocks"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { ThemePaletteIcon } from "../../components/Icons/ThemePaletteIcon"
import { SelectItemTemplate } from "../../components/MenuGroups/constants"
import { ThemePalettes } from "../../enums/theme"
import { modelIconMapping } from "../../mappings/icons"
import { setAppearance, setThemePalette, useAppDispatch, useAppSelector } from "../../store"
import { selectAppearance, selectThemePalette } from "../../store/selectors"

export const UserPreferencesView = memo(() => {
	const themePalette = useAppSelector(selectThemePalette)
	const appearance = useAppSelector(selectAppearance)
	const dispatch = useAppDispatch()

	const handleThemePaletteChange = useCallback(
		(themePalette: ThemePalettes) => {
			dispatch(setThemePalette(themePalette))
		},
		[dispatch],
	)

	const handleAppearanceChange = useCallback(
		(appearance: "light" | "dark") => {
			dispatch(setAppearance(appearance))
		},
		[dispatch],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">User Preferences</Heading>
					<Text size="sm" accentColor="base">
						Customize your Overmap experience with user preferences.
					</Text>
				</div>

				<Separator size="full" />

				<Heading size="md">Interface theme</Heading>

				<div className="flex w-full justify-between">
					<Text size="sm" accentColor="base">
						Select your interface color scheme.
					</Text>
					<Menu.Root>
						<Menu.ClickTrigger>
							<Button size="sm" variant="soft" accentColor="base">
								<div className="flex items-center gap-1">
									<ThemePaletteIcon themePalette={themePalette} />
									{themePalette}
								</div>
							</Button>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.SelectGroup<ThemePalettes>
								required
								value={themePalette}
								onValueChange={handleThemePaletteChange}
							>
								<SelectItemTemplate value={ThemePalettes.OVERMAP} data-theme={ThemePalettes.OVERMAP}>
									<ThemePaletteIcon themePalette={ThemePalettes.OVERMAP} inline />
									Forest
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.FUTURE} data-theme={ThemePalettes.FUTURE}>
									<ThemePaletteIcon themePalette={ThemePalettes.FUTURE} inline />
									Future
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.SLATE} data-theme={ThemePalettes.SLATE}>
									<ThemePaletteIcon themePalette={ThemePalettes.SLATE} inline />
									Slate
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.RUBY} data-theme={ThemePalettes.RUBY}>
									<ThemePaletteIcon themePalette={ThemePalettes.RUBY} inline />
									Ruby
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.SOLAR} data-theme={ThemePalettes.SOLAR}>
									<ThemePaletteIcon themePalette={ThemePalettes.SOLAR} inline />
									Solar
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.ENERGY} data-theme={ThemePalettes.ENERGY}>
									<ThemePaletteIcon themePalette={ThemePalettes.ENERGY} inline />
									Energy
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.HIVE} data-theme={ThemePalettes.HIVE}>
									<ThemePaletteIcon themePalette={ThemePalettes.HIVE} inline />
									Hive
								</SelectItemTemplate>

								<SelectItemTemplate
									value={ThemePalettes.TRANSFORM}
									data-theme={ThemePalettes.TRANSFORM}
								>
									<ThemePaletteIcon themePalette={ThemePalettes.TRANSFORM} inline />
									Transform
								</SelectItemTemplate>
								<SelectItemTemplate value={ThemePalettes.COPPER} data-theme={ThemePalettes.COPPER}>
									<ThemePaletteIcon themePalette={ThemePalettes.COPPER} inline />
									Copper
								</SelectItemTemplate>
							</Menu.SelectGroup>
						</Menu.Content>
					</Menu.Root>
				</div>

				<Separator size="full" />

				<Heading size="md">Color mode</Heading>

				<div className="flex w-full justify-between">
					<Text size="sm" accentColor="base">
						Select your color mode.
					</Text>
					<Menu.Root>
						<Menu.ClickTrigger>
							<Button size="sm" variant="soft" accentColor="base">
								<div className="flex items-center gap-1">
									<LuIcon
										icon={appearance === "light" ? modelIconMapping.sun : modelIconMapping.moon}
									/>
									{appearance === "light" ? "Light" : "Dark"}
								</div>
							</Button>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.SelectGroup<"light" | "dark">
								required
								value={appearance}
								onValueChange={handleAppearanceChange}
							>
								<SelectItemTemplate<"light" | "dark"> value="light">
									<LuIcon icon={modelIconMapping.sun} /> Light
								</SelectItemTemplate>
								<SelectItemTemplate<"light" | "dark"> value="dark">
									<LuIcon icon={modelIconMapping.moon} />
									Dark
								</SelectItemTemplate>
							</Menu.SelectGroup>
						</Menu.Content>
					</Menu.Root>
				</div>
			</section>
		</ViewContainer>
	)
})
UserPreferencesView.displayName = "UserPreferencesView"
